import PropTypes from 'prop-types';
import React, { Component } from 'react';
import today from 'common/utils/today';
import { validateReduxForm } from 'common/form';
import { NoteDisclosure, validations } from '@unite-us/app-components';
import {
  DateField,
  RadioField,
} from '@unite-us/ui';
import _ from 'lodash';
import SelectFieldWrapper from './SelectFieldWrapper';
import './LoginInteraction.scss';
import FormInteractionsButtons from './FormInteractionsButtons';
import AttachedToField from './AttachedToField';
import InteractionNoteField from './InteractionNoteField';
import SelectServiceCases from './SelectServiceCases';
import { SENSITIVE_GENERAL_INTERACTION_HINT_TEXT } from './constants';
import NoteVisibility from './NoteVisibility';
import {
  EMAIL_INTERACTION_TYPE,
  GENERAL_NOTE_TYPE,
  buildDurationOptions,
  buildSubmissionValues,
  getDurationFieldClassName,
  shouldShowInteractionHint,
  syncDurationForInteractionType,
} from './logInteractionUtils';

const INTERACTION_FORM = 'logInteraction';
const PROFILE_DETAIL_TYPE = 'profile';
const DEFAULT_INTERACTION_TYPE = 'phone_call';
const DEFAULT_ATTACHED_TO = 'case';
const INTERACTION_TYPE_OPTIONS = [
  {
    label: 'Phone',
    value: 'phone_call',
    style: { display: 'inline-block', width: '90px' },
    labelStyle: { width: 'calc(100% - 30px)' },
    iconStyle: { marginRight: '8px' },
  },
  {
    label: 'Email',
    value: 'email',
    style: { display: 'inline-block', width: '80px' },
    labelStyle: { width: 'calc(100% - 30px)' },
    iconStyle: { marginRight: '8px' },
  },
  {
    label: 'In person',
    value: 'meeting',
    style: { display: 'inline-block', width: '100px' },
    labelStyle: { width: 'calc(100% - 30px)' },
    iconStyle: { marginRight: '8px' },
  },
];

export class LogInteraction extends Component {
  componentDidUpdate(prevProps) {
    syncDurationForInteractionType(prevProps, this.props);
  }

  onSubmit = (values) => (
    this.props.submitFunction(buildSubmissionValues(values))
      .then(this.props.resetForm)
  );

  onCancel = () => {
    this.props.cancelFunction();
    this.props.resetForm();
  };

  render() {
    const {
      durations,
      fields,
      handleSubmit,
      isFromCaseOrReferral,
      isMyProviderSensitive,
      registerField,
      submitFailed,
      submitting,
      showAttachedToField,
      showAttachedToCases,
      showNoteVisibility,
    } = this.props;

    const hideDuration = fields.interaction.type.value === EMAIL_INTERACTION_TYPE;
    const hintText = isMyProviderSensitive ? SENSITIVE_GENERAL_INTERACTION_HINT_TEXT : '';
    const isGeneralNote = fields.interaction.attached_to.value === GENERAL_NOTE_TYPE;
    const hasCases = fields.validateCases.value;
    const shouldShowHint = shouldShowInteractionHint(isGeneralNote, hasCases, isFromCaseOrReferral);
    const durationOptions = buildDurationOptions(durations);

    const warningMessage = <NoteDisclosure />;

    return (
      <div className="log-interaction">
        <form data-role="log-interaction-form">
          <div className="row">
            <div className="col-sm-4">
              <RadioField
                field={fields.interaction.type}
                label="Interaction Type"
                options={INTERACTION_TYPE_OPTIONS}
              />
            </div>

            <div className="col-sm-4">
              <DateField
                field={fields.interaction.occurred_on}
                id="interactionDate"
                label="Interaction Date"
                ref={registerField}
                validations={validations.isRequired}
                required
                maxDate={today()}
              />
            </div>

            <div className={getDurationFieldClassName(hideDuration)}>
              <SelectFieldWrapper
                id="interactionDuration"
                className="interaction-duration-select"
                label="Duration"
                searchEnabled={false}
                field={fields.interaction.duration}
                options={durationOptions}
                ref={registerField}
                shouldValidate={!hideDuration}
                required={!hideDuration}
                shouldSort={false}
                labelKey="display_name"
                valueKey="value"
              />
            </div>
          </div>

          {
            showAttachedToField ? (
              <AttachedToField
                field={fields.interaction.attached_to}
                hidden={!hasCases}
              />
            ) : null
          }

          <InteractionNoteField
            afterLabelContent={warningMessage}
            field={fields.interaction.note}
            hintText={hintText}
            registerField={registerField}
            showHint={shouldShowHint}
          />

          {
            showNoteVisibility ? (
              <NoteVisibility
                registerField={registerField}
                disabled={isMyProviderSensitive}
                field={fields.interaction.is_note_private}
              />
            ) : null
          }

          {
            showAttachedToCases ? (
              <SelectServiceCases
                hidden={
                  !hasCases ||
                  isGeneralNote
                }
                registerField={registerField}
                serviceCases={fields.caseOptions}
                submitFailed={submitFailed}
              />
            ) : null
          }

          <FormInteractionsButtons
            cancelActionHandler={this.onCancel}
            interaction="log-interaction"
            primaryActionHandler={handleSubmit(this.onSubmit)}
            submitting={submitting}
          />
        </form>
      </div>
    );
  }
}

LogInteraction.propTypes = {
  caseOptions: PropTypes.array.isRequired,
  durations: PropTypes.array.isRequired,
  fields: PropTypes.object.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  isFromCaseOrReferral: PropTypes.bool.isRequired,
  isMyProviderSensitive: PropTypes.bool.isRequired,
  registerField: PropTypes.func.isRequired,
  resetForm: PropTypes.func.isRequired,
  submitFailed: PropTypes.bool.isRequired,
  submitFunction: PropTypes.func.isRequired,
  submitting: PropTypes.bool.isRequired,
  cancelFunction: PropTypes.func,
  showAttachedToField: PropTypes.bool,
  showAttachedToCases: PropTypes.bool,
  showNoteVisibility: PropTypes.bool.isRequired,
};

LogInteraction.defaultProps = {
  cancelFunction: _.noop,
  showAttachedToField: true,
  showAttachedToCases: true,
};

function mapStateToProps(state, ownProps) {
  const { caseOptions = [] } = ownProps;
  const isMyProviderSensitive = _.get(state, 'globalState.currentProvider.group.sensitive', null);
  const validateCases = !_.isEmpty(ownProps.caseOptions);
  const showNoteVisibility = ownProps.detailType === PROFILE_DETAIL_TYPE;
  const is_note_private = Boolean(showNoteVisibility && isMyProviderSensitive);

  return {
    durations: _.wget(state, 'session.enums.time.duration', []),
    initialValues: {
      caseOptions,
      interaction: {
        attached_to: DEFAULT_ATTACHED_TO,
        occurred_on: today(),
        type: DEFAULT_INTERACTION_TYPE,
        is_note_private,
      },
      validateCases,
    },
    isMyProviderSensitive,
    showNoteVisibility,
  };
}

const fields = [
  'interaction.is_note_private',
  'interaction.attached_to',
  'interaction.type',
  'interaction.occurred_on',
  'interaction.duration',
  'interaction.note',
  'caseOptions[].id',
  'caseOptions[].selected',
  'caseOptions[].label',
  'caseOptionSelected',
  'validateCases',
];

export default validateReduxForm({
  form: INTERACTION_FORM,
  fields,
}, mapStateToProps)(LogInteraction);
